import React, { useState } from 'react'

import type { HouseTreeNode, ProductGroup } from '../../types/catalog'
import { findProductItem, ProductPicker } from '../ProductPicker'
import { LineItemEditor } from '../LineItemEditor'
import {
  badgeForSub,
  newItemDeviceCatalogFor,
  uid,
} from './helpers'
import type { DealDraft, LineItemDraft } from './types'

interface LineItemsTabProps {
  draft: DealDraft
  patch: (next: Partial<DealDraft>) => void
  productGroups: ProductGroup[]
  houseTree: HouseTreeNode[]
  onError: (message: string) => void
}

interface AddDraft {
  productValue: string
  qty: number
  memo: string
}

const EMPTY_ADD_DRAFT: AddDraft = { productValue: '', qty: 1, memo: '' }

export const LineItemsTab: React.FunctionComponent<LineItemsTabProps> = (props) => {
  const { draft, patch, productGroups, houseTree, onError } = props

  const [showAddForm, setShowAddForm] = useState(false)
  const [productPickerOpen, setProductPickerOpen] = useState(false)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const [addDraft, setAddDraft] = useState<AddDraft>(EMPTY_ADD_DRAFT)

  function resetAddForm(): void {
    setAddDraft(EMPTY_ADD_DRAFT)
    setProductPickerOpen(false)
    setShowAddForm(false)
  }

  function commitAdd(): void {
    const item = findProductItem(productGroups, addDraft.productValue)
    if (!item) {
      onError('Please select a product.')
      return
    }
    const badge = badgeForSub(item.sub)
    const newItem: LineItemDraft = {
      id: uid(),
      type: item.sub,
      badgeLabel: badge.label,
      productValue: item.value,
      name: item.name,
      qty: Math.max(1, addDraft.qty),
      memo: addDraft.memo,
      devices: [],
      deviceCatalog: newItemDeviceCatalogFor(item.sub, item.name),
    }
    patch({ lineItems: [...draft.lineItems, newItem] })
    resetAddForm()
  }

  function setDevices(id: string, nextDevices: string[], nextCatalog: string[]): void {
    patch({
      lineItems: draft.lineItems.map((item) =>
        item.id === id
          ? {
              ...item,
              devices: nextDevices,
              deviceCatalog: nextCatalog,
              qty: nextDevices.length,
            }
          : item,
      ),
    })
  }

  function updateItem(next: LineItemDraft): void {
    patch({
      lineItems: draft.lineItems.map((item) => (item.id === next.id ? next : item)),
    })
  }

  function removeItem(id: string): void {
    patch({ lineItems: draft.lineItems.filter((item) => item.id !== id) })
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', margin: '16px 0' }}>
        <button type="button" className="btn-text" onClick={() => setShowAddForm((value) => !value)}>
          + Add Line Item
        </button>
      </div>

      <div className={`add-form${showAddForm ? ' open' : ''}`}>
        <div className="form-grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) 96px' }}>
          <div className="form-group">
            <label className="form-label">
              Product <span className="req">*</span>
            </label>
            <ProductPicker
              value={addDraft.productValue}
              onChange={(productValue) =>
                setAddDraft((current) => ({ ...current, productValue }))
              }
              isOpen={productPickerOpen}
              onOpenChange={setProductPickerOpen}
              products={productGroups}
            />
          </div>
          <div className="form-group">
            <label className="form-label">
              Quantity <span className="req">*</span>
            </label>
            <input
              className="form-input"
              type="number"
              min={1}
              value={addDraft.qty}
              onChange={(event) =>
                setAddDraft((current) => ({
                  ...current,
                  qty: Math.max(1, Number(event.target.value) || 1),
                }))
              }
            />
          </div>
          <div className="form-group full">
            <label className="form-label">Memo</label>
            <input
              className="form-input"
              placeholder="Optional notes..."
              value={addDraft.memo}
              onChange={(event) =>
                setAddDraft((current) => ({ ...current, memo: event.target.value }))
              }
            />
          </div>
        </div>
        <div className="add-form-actions">
          <button type="button" className="btn-save" onClick={commitAdd}>
            Add
          </button>
          <button type="button" className="btn-ghost" onClick={resetAddForm}>
            Cancel
          </button>
        </div>
      </div>

      <div>
        {draft.lineItems.length === 0 ? (
          <div
            style={{
              border: '1px dashed var(--border)',
              borderRadius: 10,
              padding: '32px 24px',
              textAlign: 'center',
              color: 'var(--secondary)',
              fontSize: 13,
            }}
          >
            No line items yet. Click <strong>+ Add Line Item</strong> to begin.
          </div>
        ) : (
          draft.lineItems.map((item) => (
            <LineItemEditor
              key={item.id}
              item={item}
              productGroups={productGroups}
              houseTree={houseTree}
              isDropdownOpen={openDropdownId === item.id}
              onDropdownOpenChange={(open) => setOpenDropdownId(open ? item.id : null)}
              onUpdate={updateItem}
              onDevicesChange={(nextDevices, nextCatalog) =>
                setDevices(item.id, nextDevices, nextCatalog)
              }
              onRemove={() => removeItem(item.id)}
            />
          ))
        )}
      </div>
    </>
  )
}
